// src/hooks/useAuth.js
import { useState, useEffect, createContext, useContext } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, googleProvider } from "../lib/firebase";
import { ensureProgramYearRollover, ensurePortalDesignerFromAuthUser } from "../lib/db";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null);
  const [profile, setProfile]   = useState(null); // Firestore designer doc
  const [role, setRole]         = useState(null);  // "admin" | "designer" | "contractor"
  const [loading, setLoading]   = useState(true);
  /** Set when Firestore profile reads fail (e.g. permission-denied); surfaced on Login. */
  const [profileError, setProfileError] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null); setProfile(null); setRole(null);
        setProfileError(null);
        setLoading(false);
        return;
      }
      // Keep loading true until Firestore role is resolved so guards never see user without role.
      setLoading(true);
      setUser(firebaseUser);
      setProfileError(null);

      try {
        const adminSnap = await getDoc(doc(db, "admins", firebaseUser.uid));
        if (adminSnap.exists()) {
          setRole("admin");
          setProfile({ uid: firebaseUser.uid, email: firebaseUser.email, name: "Showroom Admin" });
          return;
        }

        let designerSnap = await getDoc(doc(db, "designers", firebaseUser.uid));
        let contractorSnap = await getDoc(doc(db, "contractors", firebaseUser.uid));

        if (!designerSnap.exists() && !contractorSnap.exists()) {
          await ensurePortalDesignerFromAuthUser(firebaseUser);
          designerSnap = await getDoc(doc(db, "designers", firebaseUser.uid));
          contractorSnap = await getDoc(doc(db, "contractors", firebaseUser.uid));
        }

        if (designerSnap.exists()) {
          await ensureProgramYearRollover(firebaseUser.uid, "designers");
          const refreshed = await getDoc(doc(db, "designers", firebaseUser.uid));
          setRole("designer");
          setProfile({ uid: firebaseUser.uid, ...refreshed.data() });
        } else if (contractorSnap.exists()) {
          await ensureProgramYearRollover(firebaseUser.uid, "contractors");
          const refreshed = await getDoc(doc(db, "contractors", firebaseUser.uid));
          setRole("contractor");
          setProfile({ uid: firebaseUser.uid, ...refreshed.data() });
        } else {
          setRole(null);
          setProfile(null);
        }
      } catch (e) {
        console.error("Auth profile load failed:", e);
        const code = e?.code ?? "";
        const detail =
          code === "permission-denied"
            ? "Firestore blocked your profile (permission denied). Deploy the latest firestore.rules from the repo, or ask an admin to check security rules."
            : e?.message || "Could not load your showroom profile from the database.";
        setProfileError(detail);
        setRole(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, []);

  const loginEmail = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);

  const loginGoogle = () => signInWithPopup(auth, googleProvider);

  const logout = () => signOut(auth);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  return (
    <AuthContext.Provider value={{ user, profile, role, loading, profileError, loginEmail, loginGoogle, logout, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
